import { ChevronLeft } from "lucide-react";
import React, { useState } from "react";
import { useEscapeToBack } from "../keyboard/useEscapeToBack.ts";
import { useEmbedded } from "../../hooks/useEmbedded.tsx";
import { cn } from "../../lib/utils.ts";
import { RightSidebarProvider } from "../right-sidebar/RightSidebar.tsx";
import { SidebarNavBoundary } from "../right-sidebar/SidebarNavBoundary.tsx";
import { Button } from "../ui/button.tsx";
import { EventDeleteDialogs } from "@/components/calendars/EventDeleteDialogs.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip.tsx";
import { HeaderUtilitySlot } from "./HeaderUtilitySlot.tsx";
import { HeaderUtilities } from "./HeaderUtilities.tsx";
import { LogoNav } from "./LogoNav.tsx";
import { MainAreaPortalProvider } from "./mainAreaPortal.tsx";
import { breakWordsContainer } from "./constants.ts";
import {
  pageColumnClasses,
  screenContainer,
  ScreenInnerScrollWrapper,
  ScreenOuterScrollWrapper,
} from "./contentLayout.tsx";

/**
 * Header wrapper, handles overall layout. Pages can pass `innerClassName` to
 * override the default `h-14 @md:h-20` height row (e.g. the calendar page
 * uses a taller header to match the editorial chrome).
 */
export const SiteHeaderWrapper = ({
  children,
  innerClassName,
}: {
  children?: React.ReactNode;
  innerClassName?: string;
}) => {
  return (
    <header className={cn(screenContainer, "bg-background")}>
      <div
        className={cn(
          "h-14 @md:h-20 shrink-0 z-10 px-4 lg:px-8 flex items-center @7xl:gap-1",
          innerClassName,
        )}
      >
        {children}
        {/* Every header the app has is one of two shells, and this is the one
            screens outside the canvas use. Publishing here rather than at each
            of those screens means the utilities end the row on all of them,
            including the handful whose header is nothing but a logo. */}
        <HeaderUtilitySlot className="ml-auto" />
      </div>
    </header>
  );
};

/**
 * Standard header title, intended to be used for most screens in conjunction with @see SiteHeaderContent.
 */
export const SiteHeaderTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"h1">) => {
  return (
    <h1
      ref={ref}
      className={cn(
        `font-semibold line-clamp-1 text-base @5xl:text-xl @5xl:tracking-tight text-left pr-4`,
        className,
      )}
      {...props}
    />
  );
};

/** Container for when we need a 12-column header to be aligned with the content without overlaying the logo. */
export const SiteHeaderCentered12ColHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        /**
         * 72rem is the magic number where we can align this header to the body content without
         * having less than 16px padding between it and the logo on the left. After that we want the body and
         * header to have the same width and center alignment.
         *
         * @7xl, we need to offset more to handle the extra logo padding at that size.
         *
         * Use container queries instead of media queries to handle the sidebar more gracefully.
         *
         * @see pageColumnWidth12 we're replicating a lot of it here (e.g. 260 max-w is 268 max-w minus 8 units for the gutters
         * which are managed higher up in the site header code)
         */
        "w-full px-0 @7xl:-translate-x-5.5 @[72rem]:-translate-x-5 @[72rem]:max-w-260 @[72rem]:mx-auto",
        className,
      )}
    >
      {children}
    </div>
  );
};

/**
 * Standard header layout component, intended to be used for most screens in conjunction
 * with @see ViewWrapper.
 */
export const SiteHeaderContent = ({
  includeBackButton = false,
  onBack,
  title,
  children,
  NavComponent = LogoNav,
  menuLogoClassName,
  backTooltip = "Back",
  hideNav = false,
}: {
  includeBackButton?: boolean;
  /** For common use cases, just pass the title as a string. If you need to customize, pass children and use @see SiteHeaderTitle. */
  title?: string;
  /** For more complex cases, prefer this over title. */
  children?: React.ReactNode;
  onBack?: () => boolean;
  /** Custom nav component to use instead of LogoNav. */
  NavComponent?: typeof LogoNav;
  /** Controls when the wordmark inside the dropdown menu is hidden. Passed through to LogoNav. */
  menuLogoClassName?: string;
  /** Tooltip text for the back button. Defaults to "Back". */
  backTooltip?: string;
  /** Hide the hamburger nav entirely. Use on pages that only need a back button. */
  hideNav?: boolean;
}) => {
  // Screen-level Escape-to-back + the click handler, shared with the canvas
  // panel header's back button (see {@link useEscapeToBack}).
  const handleBackButton = useEscapeToBack(onBack);

  return (
    <SiteHeaderWrapper>
      <div className="flex items-center">
        {!hideNav && (
          <NavComponent
            className={cn("mr-2 @3xl:mr-4", includeBackButton && "@max-md:hidden")}
            menuOnly={includeBackButton}
            menuLogoClassName={menuLogoClassName}
          />
        )}
        {includeBackButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Mobile pulls the title into the button's own padding: the
                  chevron glyph fills only 6px of its 24px box, so a plain
                  margin reads as ~25px of space next to 12px icon-label gaps
                  elsewhere. -mr-1 lands the perceived gap at ~13px. */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackButton}
                className={cn("-ml-2 @3xl:ml-0", "-mr-1 @3xl:mr-4")}
                data-statsig-name="nav-back"
              >
                <ChevronLeft className="size-6" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{backTooltip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {title ? <SiteHeaderTitle>{title}</SiteHeaderTitle> : null}
      {children}
    </SiteHeaderWrapper>
  );
};

/**
 * Wrapper for standard views that just need the centered layout, screen gutters, scrolling support, etc.
 * For more complex views, use @see ScreenOuterScrollWrapper and @see ScreenInnerScrollWrapper directly.
 */
export const MainContentWrapper = ({
  children,
  columns = 12,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  /** Number of columns in our design system 12-col content grid to use. */
  columns?: keyof typeof pageColumnClasses;
}) => {
  return (
    <ScreenOuterScrollWrapper>
      <ScreenInnerScrollWrapper className={cn(pageColumnClasses[columns], className)} {...props}>
        {children}
      </ScreenInnerScrollWrapper>
    </ScreenOuterScrollWrapper>
  );
};

/**
 * Baseline top layout component, intended to be used for most screens.
 *
 * Sets up the site header, right sidebar, and main content slots. For standard main content layout,
 * use @see MainContentWrapper. For standard site header layout, use @see SiteHeaderContent.
 *
 * TODO: rename to HeaderSidebarLayout
 */
export const ViewWrapper: React.FC<{
  /**
   * The content to display in the header. When provided, renders a standard site header.
   * Typically should use @see SiteHeaderContent for the content.
   * If omitted, no header is rendered (useful for screens with custom headers).
   */
  headerContent?: React.ReactNode;
  /** The content to display in the main content area. */
  children: React.ReactNode;
  /** Whether the right sidebar should overlay if it needs to. Set to true for screens with a wide layout. */
  rightSidebarOverlayable?: boolean;
  /** Whether to hide the header when in a WebView. Defaults to true. */
  hideHeaderInWebView?: boolean;
}> = ({ headerContent, children, rightSidebarOverlayable = false, hideHeaderInWebView = true }) => {
  const { isWebView } = useEmbedded();
  const shouldHideHeader = hideHeaderInWebView && isWebView;

  // The center column is the main content region that shrinks when the right
  // sidebar opens. Expose its element so artifact lightboxes can portal into it
  // (and re-center within the shrinking space) instead of covering the viewport.
  const [centerColumnEl, setCenterColumnEl] = useState<HTMLElement | null>(null);

  return (
    <TooltipProvider delayDuration={500} skipDelayDuration={0}>
      <SidebarNavBoundary>
        <RightSidebarProvider
          className={cn(
            // min-h-0 overrides the SidebarProvider's default min-h-svh: this
            // wrapper fills its parent (h-full), and a 100svh floor would push it
            // past a shortened parent (e.g. when the reauth banner takes a slice
            // of the viewport), overflowing and hiding bottom-pinned controls.
            "h-full min-h-0 flex flex-row w-full",
            // set custom scrollbar styles. any child can opt into these with "scrollbar" or "scrollbar-thin" https://adoxography.github.io/tailwind-scrollbar/examples
            "scrollbar-track-transparent scrollbar-thumb-muted-foreground",
          )}
          overlayable={rightSidebarOverlayable}
        >
          {/* Center column. Contains main header and content. */}
          <div
            ref={setCenterColumnEl}
            className={cn(
              "flex grow flex-col relative isolate @3xl:gap-1",
              breakWordsContainer,
              // backstop @container, helps ensure things like container queries in components work
              // we should generally override this in the body of each primary surface (screens and sidebars)
              // when the sidebar is open, this container will be the width of the screen minus the sidebar width
              "@container contain-layout",
            )}
          >
            <MainAreaPortalProvider value={centerColumnEl}>
              {/* Main header. Nested in the center column to ensure its content aligns with main's. Always full bleed to the right. */}
              {!shouldHideHeader && headerContent}
              <main className={`flex-1 flex flex-col isolate min-h-0`}>{children}</main>
            </MainAreaPortalProvider>
          </div>
        </RightSidebarProvider>
      </SidebarNavBoundary>
      {!isWebView && <HeaderUtilities />}
      {/* Mounted here, with the other once-per-screen furniture, because the
          surfaces that request a delete all close as they do it — see
          `eventPendingDeleteAtom`. */}
      <EventDeleteDialogs />
    </TooltipProvider>
  );
};
